import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, TextField, Button, CircularProgress, Alert, Tabs, Tab } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function ProfilePage() {
    const { token } = useAuth();
    const [profile, setProfile] = useState({ email: '' });
    const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '' });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [tabValue, setTabValue] = useState(0);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const { data } = await axios.get('/api/users/profile', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setProfile(data);
            } catch (err) {
                setError('Failed to load profile.');
            } finally {
                setLoading(false);
            }
        };
        if (token) {
            fetchProfile();
        }
    }, [token]);

    const handleProfileChange = (e) => {
        setProfile({ ...profile, [e.target.name]: e.target.value });
    };

    const handlePasswordChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const { data } = await axios.patch('/api/users/profile', { email: profile.email }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccess(data.message);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update profile.');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const { data } = await axios.patch('/api/users/update-password', passwordData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccess(data.message);
            setPasswordData({ currentPassword: '', newPassword: '' });
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update password.');
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setError('');
        setSuccess('');
    };

    if (loading) return <CircularProgress />;

    return (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h4" gutterBottom>My Profile</Typography>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="Update Profile" />
                    <Tab label="Change Password" />
                </Tabs>
            </Box>
            
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

            <TabPanel value={tabValue} index={0}>
                <Typography variant="h6">Update Your Details</Typography>
                <form onSubmit={handleProfileSubmit}>
                    <TextField label="Username" value={profile.username || ''} fullWidth margin="normal" disabled />
                    <TextField name="email" label="Email" type="email" value={profile.email || ''} fullWidth margin="normal" required onChange={handleProfileChange} />
                    <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                </form>
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
                <Typography variant="h6">Change Your Password</Typography>
                <form onSubmit={handlePasswordSubmit}>
                    <TextField name="currentPassword" label="Current Password" type="password" value={passwordData.currentPassword} fullWidth margin="normal" required onChange={handlePasswordChange} />
                    <TextField name="newPassword" label="New Password" type="password" value={passwordData.newPassword} fullWidth margin="normal" required onChange={handlePasswordChange} />
                    <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Update Password'}
                    </Button>
                </form>
            </TabPanel>
        </Paper>
    );
}

export default ProfilePage;
